<template>
  <Dialog
    :visible="isVisible"
    modal
    :closable="false"
    :draggable="false"
    :header="$t('tenant_owner_notice.header', { tenantName })"
    :style="{ width: '460px' }"
    :breakpoints="{ '640px': '92vw' }"
    data-testid="tenant-owner-notice"
  >
    <p class="text-sm text-gray-600 break-words">
      {{ $t('tenant_owner_notice.message') }}
    </p>

    <template #footer>
      <Button
        :label="$t('tenant_owner_notice.confirm')"
        severity="primary"
        data-testid="tenant-owner-notice-confirm"
        @click="handleConfirm"
      />
    </template>
  </Dialog>
</template>

<script lang="ts">
import { defineComponent, computed, ref, watch } from 'vue';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import { useAuthStore } from '@/stores/authStore';

/**
 * UC13: the first time a person uses Connect as Tenant Owner of a tenant, he is shown the
 * notice on business and private accounts once. The flag lives on this device only, so the
 * notice appears once more on another device or browser.
 */
export const tenantOwnerNoticeKey = (sub: string, tenantId: string): string =>
  `tenant_owner_notice_seen:${sub}:${tenantId}`;

export default defineComponent({
  name: 'TenantOwnerNoticeComponent',
  components: { Dialog, Button },
  setup() {
    const authStore = useAuthStore();
    const seenKey = ref('');

    const sub = computed(() => authStore.sub || authStore.user?.userIdentifier || '');

    const tenantName = computed(() => {
      const membership = authStore.user?.tenantMemberships?.[0] as
        | { tenantName?: unknown }
        | undefined;
      return typeof membership?.tenantName === 'string' ? membership.tenantName : '';
    });

    const candidateKey = computed(() =>
      authStore.isLoggedIn && authStore.isTenantOwner && sub.value && authStore.myTenantId
        ? tenantOwnerNoticeKey(sub.value, authStore.myTenantId)
        : ''
    );

    watch(
      candidateKey,
      (key) => {
        seenKey.value = key && localStorage.getItem(key) === null ? key : '';
      },
      { immediate: true }
    );

    const isVisible = computed(() => seenKey.value !== '');

    const handleConfirm = () => {
      if (seenKey.value) localStorage.setItem(seenKey.value, '1');
      seenKey.value = '';
    };

    return { isVisible, tenantName, handleConfirm };
  },
});
</script>
